import { IStylesRule, StyleEntry } from 'Shared/backend/styleUtils';
import { CustomStyle, CustomStyleProperty, CustomStyleRule, LocationPathStep, Palette }
	from 'Shared/model/venueSettings';
import { RuleProperty, nestedSelector, ruleLocation, ruleProperties }
	from 'Shared/frontend/styles/ruleProperties';
import { resolvePropertyCss } from 'Shared/frontend/styles/settingRules';


/*
	The venue's own styles, built in the Style Builder (/admin/style-builder) and stored on the venue
	document under settings.frontend.customStyles. standardStyles() exposes the result as the whole
	'Venue styles' category - every per-venue style is built here now, none are left in code.

	Each stored rule names a location and the properties from ruleProperties() set there; this turns
	them back into the rule shape resolveStyleDetails() / ShowWrapElement consume.

	A style resolves against the style path: every property becomes an IStylesRule of its own, all
	carrying the location of the rule they were listed under - two properties both writing 'css' would
	otherwise have to be merged here, which is a job the resolver already does over separate flat
	rules. A rule whose class names an embedded-html element is the exception, resolving to a nested
	selector rather than a path - see buildRule().

	A style's 'extends' names another style of this venue's and nothing else: the code-defined
	catalogue is not inheritable, so nothing outside this list is looked at. See inheritedRules().
*/
export function venueCustomStyles(customStyles:CustomStyle[],
	palette:Palette): {[name: string]: StyleEntry}
{
	const properties = ruleProperties(palette);

	const ret: {[name: string]: StyleEntry} = {};

	for (const style of customStyles)
		ret[style.name] = {
			explanation: style.explanation != undefined && style.explanation != '' ? style.explanation :
				'A custom style for your venue, built in the Style Builder.',
			rules: inheritedRules(properties, customStyles, style, [style.name]),
		};

	return ret;
}


/*
	One named venue style as a flat css object - what the code that draws a button reads instead of
	waiting for a style tag to turn the style on.

	Only 'self' rules contribute: this css lands straight on the element the caller is building, so a
	rule pointed at a class or a path below it has nothing to resolve against and is dropped with a
	warning rather than being flattened onto the element itself. A style it inherits is flattened
	first, so the style's own properties win where the two set the same one - the order
	inheritedRules() puts them in.

	A name that is not in the list at all is a style the venue has deleted while a button style tag
	still names it in params.style. The caller's own base stands, which is the
	same thing an empty style would do. A rename is carried across by renameCustomStyle.ts, so it does
	not land here.
*/
export function namedStyleCss(customStyles:CustomStyle[], name:string, palette:Palette): object
{
	const style = customStyles.find(entry => entry.name==name);
	if (style == undefined) {
		log.warn(`Style Builder style not found: ${name}`);
		return {};
	}

	const properties = ruleProperties(palette);
	const css = {};

	for (const rule of selfRules(customStyles,style,[style.name]))
		for (const entry of rule.properties) {
			const property = properties[entry.property];
			if (property == undefined) {
				log.warn(`Style Builder property not found: ${name} / ${entry.property}`);
				continue;
			}
			Object.assign(css, resolvePropertyCss(property,entry.params));
		}

	return css;
}


/*
	The 'self' rules of a style and everything it extends, inherited first - the flat half of what
	inheritedRules() does for a style being resolved against a page. The two walk the extends chain
	the same way and stop on the same circle.
*/
function selfRules(customStyles:CustomStyle[], style:CustomStyle, seen:string[]): CustomStyleRule[]
{
	const own = style.rules.filter(rule => rule.location == 'self');

	if (own.length < style.rules.length)
		log.warn(`Style Builder style is read flat and has rules away from 'self': ${style.name}`);

	const base = style.extends;
	if (base==undefined || base=='' || seen.includes(base))
		return own;

	const venueBase = customStyles.find(entry => entry.name==base);
	if (venueBase == undefined)
		return own;

	return [...selfRules(customStyles,venueBase,[...seen,base]), ...own];
}


/*
	A style's rules, the ones it inherits first.

	Everything a style extends lands ahead of its own rules, so where the two set the same property
	at the same location the style's own wins - the resolver merges flat rules in order.

	Only the venue's own styles can be extended. A code-defined style is a shape the Style Builder
	can't express - arbitrary css, locations of its own, fields like parallax and animations - so
	inheriting one would produce a style the venue could see but not edit; a name that isn't in this
	list is a style that has been deleted or a document written before this rule.

	'seen' is the chain of names walked so far. A style that extends its way back to one of them
	would recurse forever, so the chain stops there with a warning and the style keeps just the rules
	it reached: the Style Builder won't offer an option that closes a circle, but the document could
	have been written before a rename or by hand.
*/
function inheritedRules(properties:{[name:string]:RuleProperty}, customStyles:CustomStyle[],
	style:CustomStyle, seen:string[]): IStylesRule[]
{
	const own = pageRules(properties, style);

	const base = style.extends;
	if (base==undefined || base=='')
		return own;

	if (seen.includes(base)) {
		log.warn(`Style Builder styles extend each other in a circle: ${[...seen,base].join(' -> ')}`);
		return own;
	}

	const venueBase = customStyles.find(entry => entry.name==base);
	if (venueBase == undefined) {
		log.warn(`Style Builder style extends a style that no longer exists: ${style.name} / ${base}`);
		return own;
	}

	return [...inheritedRules(properties,customStyles,venueBase,[...seen,base]), ...own];
}


/*
	The venue styles a style inherits from, nearest first - what the Style Builder offers 'extends'
	against: a style that already has this one in its chain can't be extended by it without closing a
	circle. Stops at a repeat, so a circle already in the document doesn't hang the page that is
	trying to keep the next one out.
*/
export function extendsChain(customStyles:CustomStyle[], name:string): string[]
{
	const chain: string[] = [];

	let base = customStyles.find(entry => entry.name==name)?.extends;

	while (base != undefined && base != '' && !chain.includes(base)) {
		chain.push(base);
		base = customStyles.find(entry => entry.name==base)?.extends;
	}

	return chain;
}


function pageRules(properties:{[name:string]:RuleProperty}, style:CustomStyle): IStylesRule[]
{
	return style.rules.flatMap(rule => {
		/* An empty class filter matches every component, so a class rule that never got its class
		   would style the lot rather than nothing. */
		if (rule.location=='class' && !rule.locationClass) {
			log.warn(`Style Builder rule has no class: ${style.name}`);
			return [];
		}

		const listed = rule.properties.flatMap(entry => {
			const property = properties[entry.property];
			if (!property) {
				log.warn(`Style Builder property not found: ${style.name} / ${entry.property}`);
				return [];
			}
			return [{property, entry}];
		});

		const selector = rule.location=='class'
			? nestedSelector('styleBuilder',rule.locationClass ?? '') : '';
		if (selector != '')
			return [nestedRule(listed,selector)];

		return listed.map(item => buildRule(item.property,item.entry,rule.location,
			rule.locationClass ?? '', rule.locationPath ?? []));
	});
}


/*
	The whole of one rule as a single IStylesRule, its css nested under the selector its class points
	at, for a class that names no component - see nestedSelector(). The rule lands on the styled
	component itself and ordinary css descendant matching finds the elements.

	Every property goes into the one object because applyRule() shallow-merges the css of the rules
	that apply: handed over as a rule each, two properties sharing a selector key would clobber each
	other instead of merging.
*/
function nestedRule(listed:{property:RuleProperty, entry:CustomStyleProperty}[],
	selector:string): IStylesRule
{
	return {
		css: () => ({[selector]: mergedCss(listed)}),
		location: () => ['*'],
	};
}


/* Every listed property's css in the one object, resolved when the rule is - not while the catalogue
   is being built, which is what the rest of these rules do and is before there is a page to resolve
   against. */
function mergedCss(listed:{property:RuleProperty, entry:CustomStyleProperty}[]): object
{
	const css = {};
	for (const item of listed)
		Object.assign(css, resolvePropertyCss(item.property,item.entry.params));
	return css;
}


/*
	One stored property as an IStylesRule, at the location of the rule it was listed under - for the
	locations that are a path match. A class naming no component goes through nestedRule() instead.

	The params the property's field functions want are the ones stored against it, not the ones
	resolveStyleDetails() passes in - those belong to the style tag sitting on the page, which a
	Style Builder style has no use for. Hence the wrapping: each field ignores what it is handed and
	reads the stored params instead.
*/
function buildRule(property:RuleProperty, entry:CustomStyleProperty,
	location:CustomStyleRule['location'], aClass:string, path:LocationPathStep[]): IStylesRule
{
	const fields = Object.entries(property.fields)
		.map(([field,fn]) => [field, () => fn(entry.params,false)]);

	return {
		...Object.fromEntries(fields),
		location: () => ruleLocation(location,aClass,path),
	};
}
